"use client";

import { useRef, useState } from "react";
import dynamic from "next/dynamic";
import { Icon, ImageWrapper, ProductCard } from "@farm-junction/design-library";
import PdpModelSummaryCard from "@/component/pdp/revamp-components/PdpModelSummaryCard";
import LeadButton from "@/component/ui/Lead/LeadButton";
import MobileStickyCta, { MOBILE_STICKY_CTA_CLASS } from "@/component/pdp/MobileStickyCta";

import type { ColourGalleryImage } from "./ColourGalleryPopup";
import type { GallerySubcategory } from "@/component/pdp/image-page/types";

const ColourGalleryPopup = dynamic(() => import("./ColourGalleryPopup"));

export type ModelColour = {
    id: string;
    name: string;
    hex: string;
    image?: string;
    tone?: string;
    finish?: string;
    variantCount?: number;
    variantIds?: string[];
    price?: number;
    isPromoted?: boolean;
    roofColor?: string;
};

export type ColourGalleryVariant = {
    id: string;
    name: string;
    price: string;
    href?: string;
};

export type ModelColourShowcaseLibProps = {
    brandName: string;
    modelName: string;
    modelUrl: string;
    price: string;
    images: string[];
    galleryImages?: ColourGalleryImage[];
    gallerySubcategories?: GallerySubcategory[];
    colours: ModelColour[];
    variants?: ColourGalleryVariant[];
    priceCitySlug?: string;
    priceCityName?: string;
    reviewRating?: number;
    modelId?: string;
    variantId?: string | null;
    isUpcoming?: boolean;
    useSplashPopup?: boolean;
};

export default function ModelColourShowcaseLib({
    brandName,
    modelName,
    modelUrl,
    price,
    images,
    galleryImages,
    gallerySubcategories,
    colours,
    variants = [],
    priceCitySlug = "delhi",
    priceCityName,
    reviewRating = 0,
    modelId,
    variantId,
    isUpcoming = false,
    useSplashPopup = false,
}: ModelColourShowcaseLibProps) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const mobileCardRef = useRef<HTMLDivElement>(null);
    const displayCity = priceCityName || priceCitySlug;

    if (!colours.length) return null;

    const activeIndex = Math.min(selectedIndex, colours.length - 1);
    const selectedColour = colours[activeIndex];
    const mainImage = selectedColour.image || images[0];
    const productImage = images[0] || colours[0].image || "";
    const priceHref = `${modelUrl}/price-in-${priceCitySlug}`;
    // const offersLabel = `View ${new Date().toLocaleString("default", { month: "long" })} Offers`;
    const offersLabel = `View Diwali Offers`;
    const imageCount = images.length || colours.length;

    if (!mainImage) return null;

    const showColourAt = (index: number) =>
        setSelectedIndex((index + colours.length) % colours.length);

    return (
        <section
            aria-label={`${brandName} ${modelName} colour gallery`}
            className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,280px)]"
        >
            <div className="min-w-0">
                <div className="relative h-[196px] overflow-hidden rounded-[16px] bg-[#eff0f2] md:h-[446px]">
                    <button
                        type="button"
                        onClick={() => setIsGalleryOpen(true)}
                        className="block h-full w-full cursor-pointer text-left"
                        aria-label={`Open ${brandName} ${modelName} colour gallery`}
                    >
                        <ImageWrapper
                            key={selectedColour.id}
                            src={mainImage}
                            alt={`${brandName} ${modelName} in ${selectedColour.name}`}
                            title={`${brandName} ${modelName} ${selectedColour.name}`}
                            width={832}
                            height={446}
                            fetchPriority="high"
                            loading="eager"
                            sizes="(max-width: 1024px) 100vw, 832px"
                            className="block h-full w-full object-cover"
                        />
                    </button>

                    {colours.length > 1 && (
                        <>
                            <button
                                type="button"
                                aria-label="Previous colour"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    showColourAt(activeIndex - 1);
                                }}
                                className="absolute left-3 top-1/2 z-9 hidden h-10 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md bg-[#4a4d51]/85 hover:bg-[#4a4d51] md:flex"
                            >
                                <Icon name="leftCaret" width={14} height={14} fill="#ffffff" />
                            </button>
                            <button
                                type="button"
                                aria-label="Next colour"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    showColourAt(activeIndex + 1);
                                }}
                                className="absolute right-3 top-1/2 z-9 hidden h-10 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md bg-[#4a4d51]/85 hover:bg-[#4a4d51] md:flex"
                            >
                                <Icon name="rightCaret" width={14} height={14} fill="#ffffff" />
                            </button>
                        </>
                    )}

                    <span className="pointer-events-none absolute bottom-3 left-3 z-9 inline-flex max-w-[calc(100%-6rem)] items-center gap-1.5 rounded bg-[#454545] px-2 py-[3px] text-[11px] font-medium text-white">
                        <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-white" />
                        <span className="truncate">{selectedColour.name}</span>
                    </span>
                    <span className="pointer-events-none absolute bottom-3 right-3 z-9 rounded bg-[#454545] px-2 py-[3px] text-[11px] font-medium text-white">
                        {activeIndex + 1} / {colours.length}
                    </span>
                </div>


                <div className="mt-3 flex items-center justify-start gap-2 overflow-x-auto py-1 scrollbar-hide md:mt-4 md:justify-center md:gap-3">
                    {colours.map((colour, index) => {
                        const isSelected = index === activeIndex;
                        return (
                            <button
                                key={colour.id}
                                type="button"
                                title={colour.name}
                                aria-label={`View ${colour.name}`}
                                aria-pressed={isSelected}
                                onClick={() => setSelectedIndex(index)}
                                className={`h-[51px] w-[51px] shrink-0 cursor-pointer rounded-full border-2 p-[2px] shadow-sm transition-[colors,box-shadow] md:h-[64px] md:w-[64px] md:p-[3px] shadow-[0_2px_8px_rgba(0,140,139,0.28)] ${
                                    isSelected
                                        ? "border-[#008c8b] shadow-[0_2px_8px_rgba(0,140,139,0.28)]"
                                        : "border-gray-300 hover:border-gray-400"
                                }`}
                            >
                                <span
                                    className="block h-full w-full rounded-full"
                                    style={{ background: colour.hex }}
                                />
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="flex min-w-0 flex-col gap-3">
                <div className="hidden overflow-hidden rounded-[16px] border border-gray-200 bg-white md:block">
                    <ProductCard
                        title={`${brandName} ${modelName}`}
                        slug={modelUrl}
                        productImageSrc={productImage}
                        price={price}
                        priceSuffixText={`Ex-showroom, ${priceCitySlug}`}
                        // Required prop, but an empty label keeps the card's own CTA
                        // unrendered so the LeadButton below can take its place.
                        primaryCtaLabel=""
                        specs={[
                            { label: "Colours", value: String(colours.length) },
                            { label: "Images", value: String(imageCount) },
                        ]}
                        className="w-full rounded-none border-0 bg-white font-sans"
                        slotProps={{
                            imgProps: {
                                alt: `${brandName} ${modelName}`,
                                title: `${brandName} ${modelName}`,
                                width: 480,
                                height: 270,
                                loading: "lazy",
                                fetchPriority: "low",
                                className: "h-[170px] w-full bg-[#eeeeef] object-cover",
                            },
                            productDetailsProps: {
                                className: "px-4 pb-0 pt-3",
                            },
                            headingLinkProps: {
                                className: "!mb-1",
                            },
                            headingProps: {
                                className: "text-[16px] font-semibold",
                                level: "p",
                                variant: "p",
                            },
                            priceWrapperProps: {
                                className: "!mb-3",
                            },
                            priceSuffixProps: {
                                className: "block text-[12px] text-[#737373]",
                            },
                            priceProps: {
                                className: "text-[15px] font-semibold",
                            },
                            specsItemProps: {
                                className: "!bg-white !p-0 border-t border-gray-200 pt-2",
                            },
                        }}
                    />

                    {/* ProductCard's own CTAs are not used: the primary one has to be a
                        LeadButton, and the card drops secondaryCtaProps so the outlined
                        link could not be styled through it. */}
                    <div className="px-4 pb-4 pt-3">
                        <LeadButton
                            carId={modelId}
                            variantId={variantId}
                            carName={`${brandName} ${modelName}`}
                            btnText={offersLabel}
                            is_upcoming={isUpcoming}
                            fullWidth
                            useSplashPopup={useSplashPopup}
                            className="w-full cursor-pointer rounded-lg bg-[#008c8b] py-2.5 text-[13px] font-semibold text-white hover:bg-[#007776]"
                        />
                        <a
                            href={priceHref}
                            className="mt-3 flex w-full items-center justify-center rounded-lg border border-[#008c8b] py-2.5 text-[13px] font-semibold text-[#008c8b] hover:bg-[#008c8b0d]"
                        >
                            Get On Road Price
                        </a>
                    </div>
                </div>

                <PdpModelSummaryCard
                    className="md:hidden"
                    cardRef={mobileCardRef}
                    brandName={brandName}
                    modelName={modelName}
                    href={modelUrl}
                    productImageSrc={productImage}
                    rating={reviewRating}
                    leftLabel={`Ex-showroom · ${displayCity}`}
                    leftValue={price}
                    rightLabel="Total Colours"
                    rightValue={String(colours.length)}
                    footer={
                        <>
                            <LeadButton
                                carId={modelId}
                                variantId={variantId}
                                carName={`${brandName} ${modelName}`}
                                btnText={offersLabel}
                                is_upcoming={isUpcoming}
                                fullWidth
                                useSplashPopup={useSplashPopup}
                                className="w-full cursor-pointer rounded-lg bg-[#008c8b] py-2.5 text-[13px] font-semibold text-white hover:bg-[#007776]"
                            />  
                        </>
                    }
                />

                <MobileStickyCta anchorRef={mobileCardRef}>
                    <LeadButton
                        carId={modelId}
                        variantId={variantId}
                        carName={`${brandName} ${modelName}`}
                        btnText={offersLabel}
                        is_upcoming={isUpcoming}
                        useSplashPopup={useSplashPopup}
                        className={MOBILE_STICKY_CTA_CLASS}
                    />
                </MobileStickyCta>
            </div>

            <ColourGalleryPopup
                open={isGalleryOpen}
                onClose={() => setIsGalleryOpen(false)}
                brandName={brandName}
                modelName={modelName}
                colours={colours}
                images={images}
                galleryImages={galleryImages}
                gallerySubcategories={gallerySubcategories}
                variants={variants}
                startIndex={activeIndex}
                onColourChange={setSelectedIndex}
                modelId={modelId}
                variantId={variantId}
                isUpcoming={isUpcoming}
            />
        </section>
    );
}
